import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { value: "sick", label: "Sick Leave" },
  { value: "casual", label: "Casual Leave" },
  { value: "other", label: "Other Leave" },
];

export default function Leaves() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balance, setBalance] = useState(null);
  const [balanceEmp, setBalanceEmp] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", leave_type: "casual", from_date: "", to_date: "", days: 1, reason: "" });
  const [editingBal, setEditingBal] = useState(false);
  const [balDraft, setBalDraft] = useState({ sick: 0, casual: 0, other: 0 });

  const canApprove = ["super_admin", "hr_admin", "manager"].includes(user?.role);

  const load = async () => {
    if (!activeCompanyId) return;
    const [l, e] = await Promise.all([
      api.get("/leaves", { params: { company_id: activeCompanyId } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setLeaves(l.data);
    setEmployees(e.data.items);
    const empId = balanceEmp || user?.employee_id || e.data.items[0]?.id;
    if (empId) {
      const { data } = await api.get(`/leaves/balance/${empId}`);
      setBalance(data);
      if (!balanceEmp) setBalanceEmp(empId);
    }
    // For employee role, force select to their own employee_id
    if (user?.role === "employee" && user?.employee_id) {
      setForm(f => ({ ...f, employee_id: user.employee_id }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, balanceEmp]);

  const saveBalance = async () => {
    try {
      const body = {
        sick: parseFloat(balDraft.sick),
        casual: parseFloat(balDraft.casual),
        other: parseFloat(balDraft.other),
      };
      const { data } = await api.put(`/leaves/balance/${balanceEmp}`, body);
      setBalance(data);
      setEditingBal(false);
      toast.success("Leave balance updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submit = async () => {
    if (!form.reason || form.reason.trim().length < 3) {
      toast.error("Please provide a reason for the leave (min 3 characters)");
      return;
    }
    if (!form.from_date || !form.to_date) {
      toast.error("Please select from and to dates");
      return;
    }
    try {
      const days = (new Date(form.to_date) - new Date(form.from_date)) / 86400000 + 1;
      const body = {
        ...form,
        company_id: activeCompanyId,
        days: days > 0 ? days : 1,
      };
      // Employees can only apply for themselves — backend enforces but make UI match
      if (user?.role === "employee" && user?.employee_id) {
        body.employee_id = user.employee_id;
      }
      await api.post("/leaves", body);
      toast.success("Leave applied");
      setOpen(false);
      setForm({
        employee_id: user?.role === "employee" ? (user?.employee_id || "") : "",
        leave_type: "casual",
        from_date: "",
        to_date: "",
        days: 1,
        reason: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const decide = async (id, level, status) => {
    try {
      await api.put(`/leaves/${id}`, { level, status, remarks: status });
      toast.success(`${level === "manager" ? "Manager" : "HR"} ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const pending = leaves.filter(l => l.status === "pending").length;
  const approved = leaves.filter(l => l.status === "approved").length;
  const rejected = leaves.filter(l => l.status === "rejected").length;

  const StageBadge = ({ label, value }) => {
    const color = value === "approved" ? "text-[#12B886] bg-[#E6FFF8]"
                : value === "rejected" ? "text-[#F54236] bg-[#FEE2E2]"
                : "text-[#946200] bg-[#FFF7E0]";
    return (
      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold uppercase tracking-wider ${color}`}>
        {label}: {value}
      </span>
    );
  };

  return (
    <div data-testid="leaves-page">
      <PageHeader eyebrow={activeCompany?.code} title="Leave management" subtitle="Apply, approve and track leave across the business.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="apply-leave-btn">
              <Plus size={14} className="mr-1.5" /> Apply leave
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase">Employee</Label>
                {user?.role === "employee" ? (
                  <div
                    className="rounded-sm border border-input bg-[#F1F5F9] px-3 py-2 text-sm font-mono"
                    data-testid="leave-employee-locked"
                  >
                    {(() => {
                      const me = employees.find((e) => e.id === user?.employee_id);
                      return me ? `${me.employee_code} · ${me.full_name}` : "Your account";
                    })()}
                  </div>
                ) : (
                  <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="leave-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase">Type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase">From</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({...form, from_date: e.target.value})} className="rounded-sm" data-testid="leave-from" /></div>
                <div><Label className="text-xs uppercase">To</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({...form, to_date: e.target.value})} className="rounded-sm" data-testid="leave-to" /></div>
              </div>
              <div>
                <Label className="text-xs uppercase">
                  Reason <span className="text-[#F54236]">*</span>
                </Label>
                <Textarea
                  rows={3}
                  required
                  value={form.reason}
                  onChange={(e) => setForm({...form, reason: e.target.value})}
                  className="rounded-sm"
                  placeholder="Please provide a clear reason (mandatory)"
                  data-testid="leave-reason"
                />
                <div className="text-[10px] text-[#64748B] mt-1">Required · minimum 3 characters</div>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="submit-leave">Apply</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Pending" value={pending} accent="text-[#FAB005]" />
          <StatCell label="Approved" value={approved} accent="text-[#12B886]" />
          <StatCell label="Rejected" value={rejected} accent="text-[#F54236]" />
          <StatCell label="Total" value={leaves.length} />
        </div>

        {balance && (
          <Section title="Leave balance">
            <div className="flex flex-wrap items-end gap-3 mb-3">
              {user?.role !== "employee" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569] block mb-1">Employee</label>
                  <select
                    className="rounded-sm border border-input bg-white px-3 py-1.5 text-sm w-72"
                    value={balanceEmp}
                    onChange={(e) => { setBalanceEmp(e.target.value); setEditingBal(false); }}
                    data-testid="balance-emp-select"
                  >
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</option>
                    ))}
                  </select>
                </div>
              )}
              {canApprove && (
                editingBal ? (
                  <div className="flex items-end gap-2">
                    <button onClick={saveBalance} className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-sm" data-testid="save-balance">Save</button>
                    <button onClick={() => setEditingBal(false)} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-border hover:bg-[#F1F5F9] rounded-sm">Cancel</button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setBalDraft({ sick: balance.sick || 0, casual: balance.casual || 0, other: balance.other || 0 }); setEditingBal(true); }}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF] rounded-sm"
                    data-testid="edit-balance"
                  >Edit balance</button>
                )
              )}
            </div>
            <div className="grid grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
              {["sick", "casual", "other"].map((k) => (
                <div key={k} className="p-4 bg-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">{k} leave</div>
                  {editingBal ? (
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="365"
                      className="font-display text-2xl mt-2 w-full bg-[#F8FAFC] border border-[#2563EB] rounded-sm px-2 py-1 focus:outline-none"
                      value={balDraft[k]}
                      onChange={(e) => setBalDraft({ ...balDraft, [k]: e.target.value })}
                      data-testid={`bal-input-${k}`}
                    />
                  ) : (
                    <div className="font-display text-2xl mt-2" data-testid={`bal-${k}`}>{balance[k] ?? 0}</div>
                  )}
                  <div className="text-[10px] text-[#94A3B8] mt-1">days available</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#64748B] mt-2">
              Auto-accrual: 1.0 Casual + 0.5 Sick per month. Last accrued: <span className="font-mono">{balance.last_accrued_month || "—"}</span>
            </div>
          </Section>
        )}

        <Section title={`${leaves.length} leave requests`} dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead>
              <TableHead>Days</TableHead><TableHead>Reason</TableHead>
              <TableHead>Approval flow</TableHead>
              {canApprove && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {leaves.map((l) => {
                const e = empMap[l.employee_id] || {};
                const ms = l.manager_status || "pending";
                const hs = l.hr_status || "pending";
                const canActManager = ["super_admin", "hr_admin", "manager"].includes(user?.role) && ms === "pending";
                const canActHr = ["super_admin", "hr_admin"].includes(user?.role) && hs === "pending" && ms === "approved";
                return (
                  <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                    <TableCell>
                      <div className="text-sm font-medium">{e.full_name || "—"}</div>
                      <div className="text-xs text-[#64748B] font-mono">{e.employee_code}</div>
                    </TableCell>
                    <TableCell className="text-xs uppercase tracking-wider">{l.leave_type}</TableCell>
                    <TableCell className="font-mono text-xs">{l.from_date}</TableCell>
                    <TableCell className="font-mono text-xs">{l.to_date}</TableCell>
                    <TableCell>{l.days}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{l.reason}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <StageBadge label="Mgr" value={ms} />
                        <StageBadge label="HR" value={hs} />
                      </div>
                    </TableCell>
                    {canApprove && (
                      <TableCell className="text-right space-y-1">
                        {canActManager && (
                          <div className="flex justify-end gap-1">
                            <button onClick={() => decide(l.id, "manager", "approved")} className="px-2 py-0.5 text-[10px] uppercase font-semibold bg-[#E6FFF8] text-[#0B6F55] hover:bg-[#12B886]/20 rounded-sm" data-testid={`mgr-approve-${l.id}`}>Mgr ✓</button>
                            <button onClick={() => decide(l.id, "manager", "rejected")} className="px-2 py-0.5 text-[10px] uppercase font-semibold bg-[#FEE2E2] text-[#991B1B] hover:bg-[#F54236]/20 rounded-sm" data-testid={`mgr-reject-${l.id}`}>Mgr ✗</button>
                          </div>
                        )}
                        {canActHr && (
                          <div className="flex justify-end gap-1">
                            <button onClick={() => decide(l.id, "hr", "approved")} className="px-2 py-0.5 text-[10px] uppercase font-semibold bg-[#E6FFF8] text-[#0B6F55] hover:bg-[#12B886]/20 rounded-sm" data-testid={`hr-approve-${l.id}`}>HR ✓</button>
                            <button onClick={() => decide(l.id, "hr", "rejected")} className="px-2 py-0.5 text-[10px] uppercase font-semibold bg-[#FEE2E2] text-[#991B1B] hover:bg-[#F54236]/20 rounded-sm" data-testid={`hr-reject-${l.id}`}>HR ✗</button>
                          </div>
                        )}
                        {!canActManager && !canActHr && l.status === "approved" && (
                          <span className="text-[10px] uppercase font-semibold text-[#12B886]">Approved</span>
                        )}
                        {!canActManager && !canActHr && l.status === "rejected" && (
                          <span className="text-[10px] uppercase font-semibold text-[#F54236]">Rejected</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
